import { SiteInfo } from '../views/CalendarMultiView'
import { GanttMilestone } from '../types/gantt.types'
import { useState, useRef, useEffect } from 'react'
import { DEFAULT_MILESTONE_TYPE_NAMES } from '../constants/gantt.const'
import { apiClient } from '@indipia/shared'
import { toast } from 'sonner'
import { formatISO, addDays } from '../constants/gantt.logic'
import { GanttUser } from '../types/gantt.types'
import { MilestoneTypeCode } from '../context/CommonCodesContext'
import { markerWarningText } from '../constants/periodSync.logic'
// 마일스톤 CRUD · 비고 저장 · 라벨 드래그 — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttMilestonesArgs = {
  selectedSite: SiteInfo | undefined
  user: GanttUser
  remarkDraft: string
  setScheduleRemark: React.Dispatch<React.SetStateAction<string>>
  milestoneTypeOptions: MilestoneTypeCode[]
  milestones: GanttMilestone[]
  setMilestones: React.Dispatch<React.SetStateAction<GanttMilestone[]>>
  setGanttBounds: React.Dispatch<React.SetStateAction<{ start: Date; end: Date; }>>
  visibleDayRangeRef: React.MutableRefObject<[number, number]>
  DWRef: React.MutableRefObject<number>
  milestonesRef: React.MutableRefObject<GanttMilestone[]>
  /** 마커 추가·수정·삭제·드래그 성공 뒤 — 공사완료 마커는 서버가 종료일도 함께 바꾸므로 현장 정보(종료일·D-day)를 다시 읽는다 */
  onMarkerChanged?: () => void
}

export function useGanttMilestones({
  selectedSite, user, remarkDraft, setScheduleRemark, milestoneTypeOptions, milestones, setMilestones,
  setGanttBounds, visibleDayRangeRef, DWRef, milestonesRef, onMarkerChanged,
}: UseGanttMilestonesArgs) {
  const [draggingMsId,     setDraggingMsId]     = useState<number | null>(null)
  const [draggingMsDelta,  setDraggingMsDelta]  = useState(0)
  const [showMilestonePop, setShowMilestonePop] = useState(false)
  const [mileSaving,       setMileSaving]       = useState(false)
  const [mileDeleting,     setMileDeleting]     = useState<number | null>(null)
  const [mileFormError,    setMileFormError]    = useState('')
  // 마커 변경 응답 warnings 의 정보 한 줄(종료일 앞당김·마커 삭제/유형 변경 시 종료일 유지) — 막지 않고 알려만 준다(C-2)
  const [markerNotice,     setMarkerNotice]     = useState<string | null>(null)
  const [mileForm,         setMileForm]         = useState<{
    milestoneType: string | null
    milestoneName: string
    milestoneDate: string
  }>({
    milestoneType: 'MOVE_IN',
    milestoneName: DEFAULT_MILESTONE_TYPE_NAMES.MOVE_IN,
    milestoneDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` })(),
  })

  const [remarkSaving,    setRemarkSaving]    = useState(false)
  const [remarkHidden,    setRemarkHidden]    = useState(false)
  const [editingMsId,   setEditingMsId]   = useState<number | null>(null)
  const [mileEditForm,  setMileEditForm]  = useState<{ milestoneType: string | null; milestoneName: string; milestoneDate: string }>({ milestoneType: '', milestoneName: '', milestoneDate: '' })
  const [mileEditing,   setMileEditing]   = useState(false)
  const msDragCtxRef   = useRef<{ msId: number; startX: number; day: number } | null>(null)

  // ── Milestone popup handlers ──────────────────────────────────
  const handleRemarkSave = async () => {
    if (!selectedSite?.scheduleId) return
    setRemarkSaving(true)
    const hdrs = {
      ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    try {
      await apiClient.put(`/schedule/schedules/${selectedSite.scheduleId}`, { remark: remarkDraft }, { headers: hdrs })
      setScheduleRemark(remarkDraft)
    } catch {
      toast.error('저장에 실패했습니다.')
    } finally {
      setRemarkSaving(false)
    }
  }

  const isMileInvalid = !mileForm.milestoneDate

  const handleMileAdd = async () => {
    if (!mileForm.milestoneDate) { setMileFormError('날짜를 입력하세요'); return }
    if (!selectedSite?.scheduleId) return
    const label = milestoneTypeOptions.find((o: { typeCode: string }) => o.typeCode === mileForm.milestoneType) as { typeName: string } | undefined
    const newName = mileForm.milestoneName || label?.typeName || mileForm.milestoneType || ''
    const isDup = milestones.some(m => m.milestoneDate === mileForm.milestoneDate && m.milestoneName === newName)
    if (isDup) { setMileFormError('동일한 날짜에 동일한 라벨의 마일스톤이 이미 있습니다'); return }
    setMileSaving(true); setMileFormError('')
    const hdrs = {
      ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    try {
      const res = await apiClient.post<{ data: GanttMilestone & { warnings?: unknown } }>(
        `/schedule/schedules/${selectedSite.scheduleId}/milestones`,
        {
          milestoneType: mileForm.milestoneType,
          milestoneName: newName,
          milestoneDate: mileForm.milestoneDate,
        },
        { headers: hdrs }
      )
      const newMs = res.data.data
      setMarkerNotice(markerWarningText(newMs.warnings))
      onMarkerChanged?.()
      setMilestones(prev => [...prev, newMs])
      const mTime = new Date(newMs.milestoneDate).getTime()
      setGanttBounds(prev => ({
        start: new Date(Math.min(prev.start.getTime(), mTime)),
        end:   new Date(Math.max(prev.end.getTime(),   mTime)),
      }))
      const defType = milestoneTypeOptions[0]?.typeCode ?? 'MOVE_IN'
      const defOpt  = milestoneTypeOptions.find((o: { typeCode: string; typeName: string }) => o.typeCode === defType) as { typeName: string } | undefined
      const today   = new Date()
      setMileForm({
        milestoneType: defType,
        milestoneName: defOpt?.typeName ?? DEFAULT_MILESTONE_TYPE_NAMES[defType] ?? defType,
        milestoneDate: `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`,
      })
    } catch {
      setMileFormError('저장 중 오류가 발생했습니다.')
    } finally {
      setMileSaving(false)
    }
  }

  const handleMileDelete = async (milestoneId: number) => {
    if (!selectedSite?.scheduleId) return
    setMileDeleting(milestoneId)
    const hdrs = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    try {
      const res = await apiClient.delete<{ data?: { warnings?: unknown } }>(
        `/schedule/schedules/${selectedSite.scheduleId}/milestones/${milestoneId}`,
        { headers: hdrs }
      )
      setMarkerNotice(markerWarningText(res.data?.data?.warnings))
      onMarkerChanged?.()
      setMilestones(prev => prev.filter(m => m.milestoneId !== milestoneId))
    } catch {
      toast.error('삭제 중 오류가 발생했습니다.')
    } finally {
      setMileDeleting(null)
    }
  }

  // ── Milestone edit ─────────────────────────────────────────────
  const handleMileEditOpen = (m: GanttMilestone) => {
    setEditingMsId(m.milestoneId)
    setMileEditForm({ milestoneType: m.milestoneType ?? '', milestoneName: m.milestoneName, milestoneDate: m.milestoneDate.slice(0, 10) })
    setMileFormError('')
  }

  const handleMileEditSave = async (milestoneId: number) => {
    if (!selectedSite?.scheduleId || !mileEditForm.milestoneDate) { setMileFormError('날짜를 입력하세요'); return }
    setMileEditing(true); setMileFormError('')
    const hdrs = { ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}), ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}) }
    try {
      const label = (milestoneTypeOptions.find((o: { typeCode: string }) => o.typeCode === mileEditForm.milestoneType) as { typeName: string } | undefined)?.typeName
      const savedName = mileEditForm.milestoneName || label || mileEditForm.milestoneType || ''
      const res = await apiClient.patch<{ data?: { warnings?: unknown } }>(
        `/schedule/schedules/${selectedSite.scheduleId}/milestones/${milestoneId}`,
        { milestoneType: mileEditForm.milestoneType, milestoneName: savedName, milestoneDate: mileEditForm.milestoneDate },
        { headers: hdrs }
      )
      setMarkerNotice(markerWarningText(res.data?.data?.warnings))
      onMarkerChanged?.()
      setMilestones(prev => prev.map(m => m.milestoneId === milestoneId
        ? { ...m, milestoneType: mileEditForm.milestoneType, milestoneName: savedName, milestoneDate: mileEditForm.milestoneDate }
        : m
      ))
      setEditingMsId(null)
    } catch {
      setMileFormError('수정 중 오류가 발생했습니다.')
    } finally {
      setMileEditing(false)
    }
  }

  // ── Milestone drag ─────────────────────────────────────────────
  const onMilestoneLabelMouseDown = (e: React.MouseEvent, msId: number, day: number) => {
    e.stopPropagation()
    e.preventDefault()
    msDragCtxRef.current = { msId, startX: e.clientX, day }
    setDraggingMsId(msId)
    setDraggingMsDelta(0)
  }

  // 드래그로 이동시키는 날짜(day + delta)가 화면에 보이는 범위를 벗어나지 않도록 delta를 제한한다.
  // 1주/2주/월 뷰에서는 화면 밖으로 나가면 마일스톤 라벨이 스크롤 영역 클리핑을 벗어나
  // 옆 패널 위에 겹쳐 보이는 문제가 있었다.
  const clampMsDelta = (day: number, rawDelta: number) => {
    const [minDay, maxDay] = visibleDayRangeRef.current
    return Math.max(minDay - day, Math.min(maxDay - day, rawDelta))
  }

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!msDragCtxRef.current) return
      const rawDelta = Math.round((e.clientX - msDragCtxRef.current.startX) / DWRef.current)
      setDraggingMsDelta(clampMsDelta(msDragCtxRef.current.day, rawDelta))
    }
    const onUp = async (e: MouseEvent) => {
      const ctx = msDragCtxRef.current
      if (!ctx) return
      const rawDelta = Math.round((e.clientX - ctx.startX) / DWRef.current)
      const delta = clampMsDelta(ctx.day, rawDelta)
      msDragCtxRef.current = null
      setDraggingMsId(null)
      setDraggingMsDelta(0)
      if (delta === 0) return
      const ms = milestonesRef.current.find(m => m.milestoneId === ctx.msId)
      const sid = selectedSite?.scheduleId
      if (!ms || !sid) return
      const newDateStr = formatISO(addDays(new Date(ms.milestoneDate), delta))
      setMilestones(prev => prev.map(m =>
        m.milestoneId === ctx.msId ? { ...m, milestoneDate: newDateStr } : m
      ))
      try {
        const res = await apiClient.patch<{ data?: { warnings?: unknown } }>(
          `/schedule/schedules/${sid}/milestones/${ctx.msId}`,
          { milestoneType: ms.milestoneType, milestoneName: ms.milestoneName, milestoneDate: newDateStr }
        )
        setMarkerNotice(markerWarningText(res.data?.data?.warnings))
        onMarkerChanged?.()
      } catch {
        setMilestones(prev => prev.map(m =>
          m.milestoneId === ctx.msId ? { ...m, milestoneDate: ms.milestoneDate } : m
        ))
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [selectedSite?.scheduleId])

  return {
    setShowMilestonePop, draggingMsId, draggingMsDelta, onMilestoneLabelMouseDown, remarkHidden,
    setRemarkHidden, remarkSaving, handleRemarkSave, showMilestonePop, editingMsId, mileEditForm,
    setMileEditForm, setEditingMsId, setMileFormError, mileEditing, handleMileEditSave, handleMileEditOpen,
    mileDeleting, handleMileDelete, mileForm, setMileForm, mileFormError, isMileInvalid, mileSaving,
    handleMileAdd, markerNotice, setMarkerNotice,
  }
}
